import { mergeQueryParameters } from '@packages/shared';
import { useRouter } from 'next/router';
import { defineMessage, useIntl } from 'react-intl';
import type { CategoryNavigationItem, ParentNavigation } from '../../api/rest/types';
import { extractDisplayMode, extractOrder, extractSelectedFilterValues } from '../../queryEncoding';
import type { CategoryFilterItem } from '../CategoryFilterStandalone';

const allCategoriesMessage = defineMessage({
  id: 'search.filters.category.allCategories',
  defaultMessage: 'Alle Kategorien',
});

const flattenChildren = (items: CategoryNavigationItem[], item: CategoryNavigationItem) => {
  items.push(item);
  if (item.children) {
    items.push(...item.children.reduce(flattenChildren, []));
  }

  return items;
};

export const useCategoryDataForFilterNavigationMobile = (
  items: CategoryNavigationItem[],
  root: ParentNavigation,
): CategoryFilterItem[] => {
  const { query } = useRouter();
  const { formatMessage } = useIntl();

  // flatten items for easier handling
  const flattenedCategories = items.reduce(flattenChildren, []);

  const selectedItems = flattenedCategories.filter((x) => x.isSelected);

  const current = selectedItems.length ? selectedItems[selectedItems.length - 1] : null;
  const parents = selectedItems
    .slice(0, -1)
    .map((parent) => ({ ...parent, isParentSelected: true, isSelected: false }));

  // check if current category has children otherwise use siblings from parent
  const otherItems =
    current && current.children?.length
      ? [current, ...current.children]
      : parents[parents.length - 1]?.children ?? [];

  // sort to always have selected on top of the list
  const sortedItems = [...otherItems].sort((childItem) => (childItem.isSelected ? -1 : 1));

  // bundle all categories together in correct order
  const categories = [
    // manually add link to home
    {
      id: '0',
      name: formatMessage(allCategoriesMessage),
      level: 0,
      isParentSelected: true,
      isSelected: false,
      uriPath: '/',
    },
    { ...root, isParentSelected: true, isSelected: false },
    ...parents,
    ...sortedItems,
  ];

  return categories.map(({ id, name, level, isParentSelected, isSelected, uriPath = '' }) => ({
    id,
    name,
    level,
    // only keep user settings in href for child categories
    href: isParentSelected
      ? uriPath
      : mergeQueryParameters(uriPath, {
          ...extractDisplayMode(query),
          ...extractOrder(query),
          ...extractSelectedFilterValues(query),
        }),
    uriPath,
    isSelected: isParentSelected || isSelected,
    isSelectedExact: isSelected,
  }));
};
